import {grey, lightBlue, red} from "@mui/material/colors";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import codeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import { assign, entries, isEqual, isUndefined, replace, size, split, keys, isEmpty, isNull, values, isArray } from "lodash";
import dateUtils from "@plannel/utils/date-util";
import GridExportSetting from "@plannel/components/aggrid/GridExportSetting";
import { Box, Typography } from "@mui/material";
import { t } from "i18next";
import { FS } from "@plannel/utils/font-util";
 

const { dayOfWeek, getCurrencySymbol } = codeUtil;
const measureCode = codeUtil.dpMeasure;
const bucketCode = codeUtil.buckets;
const { PRICE, QTY, AMT } = codeUtil.dpMeasureType;
const editableColor = lightBlue[100];
const alertColor = red[100];
const FIRST_DAY = 1;
const excelStyles =
[
  { id: "stringType", dataType: "String" },
  {
    id: "header",
    alignment: {
      vertical: "Center",
    },
    interior: {
      color: grey[100],
      pattern: "Solid",
    },
    font: {
      bold: true,
      size: 13,
    },
    borders: {
      borderBottom: {
        color: grey[900],
        lineStyle: "Continuous",
        weight: 1,
      },
    },
  },
  {
    id: "editableBackgroud",
    interior: {
      color: editableColor,
      pattern: "Solid",
    },
  },
  {
    id: "alertBackground",
    interior: {
      color: alertColor,
      pattern: "Solid",
    },
  },
  {
    id: "noteAlert",
    borders: {
      borderLeft: {
        color: "#4caf50",
        lineStyle: "Continuous",
        weight: 3,
      },
    },
  },
];

const getContextMenuItems = (params) => {
  const defaultContextMenuItems = [];
  for(const item of params.defaultItems) {
    if(item !== "paste") {
      defaultContextMenuItems.push(item);
    }
  }

  return defaultContextMenuItems;
}

const defaultGridSetting = (title) => ({
  undoRedoCellEditing: true,
  undoRedoCellEditingLimit: 20,
  enableRangeSelection: true,
  defaultColDef: {
    sortable: false,
    filter: "agTextColumnFilter",
    resizable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
  columnTypes: {
    numberColumn: {
      width: 150,
      filter: "agNumberColumnFilter",
      valueFormatter: data => NumberUtil.formatNumberData(data, { maximumFractionDigits: 2 }),
    },
    decimalPointColumn: {
      //Default Decimal Point: 2
      width: 150,
      filter: "agNumberColumnFilter",
      valueFormatter: (data) => {
        if (isNull(data?.value) || isUndefined(data?.value) || !isFinite(data?.value)) {
          return "";
        }
        return NumberUtil.formatNumberData(data, { maximumFractionDigits: 2 })
      }
    },
    percentColumn: {
      width: 150,
      filter: "agNumberColumnFilter",
      valueFormatter: data => NumberUtil.formatNumberData(data, { style: "percent", maximumFractionDigits: 2 }),
    },
    defaultValueParser: {
      valueParser: (data) => {
        if (isNull(data?.newValue) || isUndefined(data?.newValue)) {
          return null;
        }
        return data.newValue;
      }
    },
    defaultValueFormatter: {
      valueFormatter: (data) => {
        if (isNull(data.value) || isUndefined(data.value)) {
          return "";
        }
        return data.value;
      }
    },
  },
  defaultExcelExportParams: GridExportSetting.defaultExcelExportParams(title),
  defaultCsvExportParams: GridExportSetting.defaultCsvExportParams(title),
  getContextMenuItems,
});

const ableDayOfWeek = (e, selectTD, startDay) => {

  if (codeUtil.isWeekBucket(selectTD) && startDay) {
    if (startDay) {
      if (selectTD === bucketCode.PARTIAL_WEEK && e.getDate() === FIRST_DAY) {
        return false;
      }
      if (startDay === dayOfWeek.MONDAY.cd) {
        return e.getDay() !== 1;
      } else if (startDay === dayOfWeek.TUESDAY.cd) {
        return e.getDay() !== 2;
      } else if (startDay === dayOfWeek.WEDNESDAY.cd) {
        return e.getDay() !== 3;
      } else if (startDay === dayOfWeek.THURSDAY.cd) {
        return e.getDay() !== 4;
      } else if (startDay === dayOfWeek.FRIDAY.cd) {
        return e.getDay() !== 5;
      } else if (startDay === dayOfWeek.SATURDAY.cd) {
        return e.getDay() !== 6;
      } else if (startDay === dayOfWeek.SUNDAY.cd) {
        return e.getDay() !== 0;
      }
    } else {
      return e.getDay() !== 1;
    }
  }
  return false;
};

const ableEndDayOfWeek = (e, selectTD, startDay) => {
  if (codeUtil.isWeekBucket(selectTD) && startDay) {
    if (!isNull(startDay)) {
      const lastDay = dateUtils.endOfMonthDate(e);
      if (selectTD === bucketCode.PARTIAL_WEEK && e.getDate() === lastDay.getDate()) {
        return false;
      }
      if (startDay === dayOfWeek.MONDAY.cd) {
        return e.getDay() !== 0;
      } else if (startDay === dayOfWeek.TUESDAY.cd) {
        return e.getDay() !== 1;
      } else if (startDay === dayOfWeek.WEDNESDAY.cd) {
        return e.getDay() !== 2;
      } else if (startDay === dayOfWeek.THURSDAY.cd) {
        return e.getDay() !== 3;
      } else if (startDay === dayOfWeek.FRIDAY.cd) {
        return e.getDay() !== 4;
      } else if (startDay === dayOfWeek.SATURDAY.cd) {
        return e.getDay() !== 5;
      } else if (startDay === dayOfWeek.SUNDAY.cd) {
        return e.getDay() !== 6;
      }
    } else {
      return e.getDay() !== 1;
    }
  }
  return false;
};

const ableMonthOfQuerter = (e, selectTD)  => {
  if (isEqual(selectTD, bucketCode.QUARTER)) {
    return !(e.getMonth() === 0 || e.getMonth() === 3 || e.getMonth() === 6 || e.getMonth() === 9);
  }
  return false;
}

const ableEndMonthOfQuerter = (e, selectTD)  => {
  if (isEqual(selectTD, bucketCode.QUARTER)) {
    return !(e.getMonth() === 2 || e.getMonth() === 5 || e.getMonth() === 8 || e.getMonth() === 11)
  }
  return false;
}

const getSearchFormat = (date, timeDimension) => {
  if (date && typeof date !== "string") {
    if (codeUtil.isWeekBucket(timeDimension)) {
      return new ZDate(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    } else if (timeDimension === bucketCode.MONTH) {
      return new ZDate(date.getFullYear(), date.getMonth(), 1, 0, 0, 0);
    } else if (timeDimension === bucketCode.QUARTER) {
      if (date.getMonth() < 3) {
        return new ZDate(date.getFullYear(), 0, 1, 0, 0, 0);
      } else if (date.getMonth() >= 3 && date.getMonth() < 6) {
        return new ZDate(date.getFullYear(), 3, 1, 0, 0, 0);
      } else if (date.getMonth() >= 6 && date.getMonth() < 9) {
        return new ZDate(date.getFullYear(), 6, 1, 0, 0, 0);
      }
      return new ZDate(date.getFullYear(), 9, 1, 0, 0, 0);
    } else if (timeDimension === bucketCode.DAY) {
      return new ZDate(date);
    }
    return new ZDate(date.getFullYear(), 0, 1, 0, 0, 0);
  }
  return date;
};

const setChartDatas = (pivotDatas, formatHeaderList, enabledChartMeasure, editableMeasure, defaultSymbol) => {
  const datas = [];
  if (!pivotDatas || isEmpty(pivotDatas)) {
    return datas;
  }
  for (let idx = 0; idx < formatHeaderList.length; idx++) {
    const header = formatHeaderList[idx];
    const row = {
      baseDate: header,
    };

    const expression = pivotDatas[0].customerCd ? pivotDatas[0].customerCurrencyCd : pivotDatas[0].hrchyCurrencyCd;
    const symbol = getCurrencySymbol(expression) ?? defaultSymbol ?? "";
    for (const result of pivotDatas) {
      if (enabledChartMeasure?.current?.length <= 0) {
        if ((editableMeasure.current.includes(result.measure?.measureCd) && result.measure?.valueTypeCd === AMT)) {
          const measureNm = result.measure?.measureCd;
          if (result?.pivotData?.length > 0) {
            if (row[measureNm]) {
              row[measureNm] = row[measureNm] + (isUndefined(result.pivotData[idx]) ? 0 : Math.round(result.pivotData[idx]));
            } else {
              row[measureNm] = isUndefined(result.pivotData[idx]) ? 0 : Math.round(result.pivotData[idx]);
            }
          } else {
            if (row[measureNm] === undefined || row[measureNm] === null) {
              row[measureNm] = 0;
            }
          }
        }
      } else {
        if (enabledChartMeasure?.current?.includes(result.measure?.measureCd)) {
          const measureNm = result.measure?.measureCd;
          if (result?.pivotData?.length > 0) {
            if (row[measureNm]) {
              row[measureNm] = row[measureNm] + (isUndefined(result.pivotData[idx]) ? 0 : Math.round(result.pivotData[idx]));
            } else {
              row[measureNm] = isUndefined(result.pivotData[idx]) ? 0 :Math.round(result.pivotData[idx]);
            }
          } else {
            if (row[measureNm] === undefined || row[measureNm] === null) {
              row[measureNm] = 0;
            }
          }
        }
      }
    }
    row.symbol = symbol;
    if (size(row) > 0) {
      datas.push(row);
    }
  }
  return datas;
};

// recharts ChartComposed 용 dataSeries 어댑터.
// 셀 클릭으로 만든 chartData(baseDate × measure)를 measure별 차트타입(LINE/BAR/AREA)으로 변환한다.
// 색상은 주입하지 않는다 — ChartComposed 가 measure별로 colorByMeasure(ColorUtil)로 stroke/그라데이션 fill 을
// 일관되게 칠하므로, 여기서 typeOptions.fill 을 강제하면 라인(colorSet)과 fill(colorByMeasure)이 어긋난다.
const chartDataSeries = (chartP13nData, chartData, enabledChartMeasure, measure) => {
  const series = [];
  if (!chartData || isEmpty(chartData)) {
    return series;
  }

  // 같은 단위(valueTypeCd)의 measure 는 하나의 Y축을 공유하도록 groupName 으로 묶는다
  // (미설정 시 measure 마다 축이 생겨 왼쪽에 축이 중복 표시됨). 처음 등장한 단위=왼쪽, 그 외=오른쪽.
  const orientationByUnit = {};
  const orientationOf = (unit) => {
    if (!orientationByUnit[unit]) {
      orientationByUnit[unit] = size(orientationByUnit) === 0 ? "left" : "right";
    }
    return orientationByUnit[unit];
  };

  for (const measureNm of keys(chartData[0])) {
    if (measureNm === "baseDate" || measureNm === "symbol") {
      continue;
    }
    if (!measure[measureNm]) {
      continue;
    }
    let type;
    if (enabledChartMeasure?.current?.length <= 0) {
      type = codeUtil.toggleType.BAR;
    } else {
      const chartMeasure = chartP13nData?.chart?.[measureNm];
      if (!chartMeasure) {
        continue;
      }
      if (chartMeasure.chartType === codeUtil.dpChartType.LINE) {
        type = codeUtil.toggleType.LINE;
      } else if (chartMeasure.chartType === codeUtil.dpChartType.BAR) {
        type = codeUtil.toggleType.BAR;
      } else if (chartMeasure.chartType === codeUtil.dpChartType.DASHED) {
        type = codeUtil.toggleType.DASHED;
      } else {
        // ⚠️ 여기는 "AREA" 전용 분기가 아니라 **나머지 전부**다. 새 차트 타입을 모달에만 추가하면
        //   조용히 영역 차트가 된다 — dpChartType 과 이 분기를 항상 같이 고칠 것.
        type = codeUtil.toggleType.AREA;
      }
    }
    const unit = measure[measureNm]?.valueTypeCd || "";
    series.push({
      field: measureNm,
      text: GridUtils.gridValueL10N(measure[measureNm]?.name),
      formatter: NumberUtil.formatNumber,
      type,
      groupName: unit,
      orientation: orientationOf(unit),
    });
  }
  return series;
};

const getComputeObj = (results, formatHeaderList) => {
  const computeObj = {};
  const computeMeasure = [measureCode.ASP.cd, measureCode.BF_QTY.cd];
  for (const result of results) {
    const { customerHrchyCd: custCd, itemHrchyCd: itemCd } = result;
    if (computeMeasure.includes(result.measureCd)) {
      if (computeObj[`${custCd},${itemCd}`]) {
        computeObj[`${custCd},${itemCd}`] = assign(computeObj[`${custCd},${itemCd}`], { [result.measureCd]: result.pivotData });
      } else {
        computeObj[`${custCd},${itemCd}`] = { [result.measureCd]: result.pivotData };
      }
    }
  }

  for (const [key, val] of entries(computeObj)) {
    const bfAmt = [];
    if (val[computeMeasure[0]] && val[computeMeasure[1]]) {
      for (let i = 0; i < formatHeaderList.length; i++) {
        if (!isNaN(val[computeMeasure[0]][i] && !isNaN(val[computeMeasure[1]][i]))) {
          bfAmt.push(val[computeMeasure[0]][i] * val[computeMeasure[1]][i]);
        } else {
          bfAmt.push(0);
        }
      }
    }
    assign(computeObj[key], { bfAmt });
  }

  return computeObj;
}

const setAttrGridData = (row, result) => {
  const displayParCustHrchy = "displayParCustHrchy";
  const displayParItemHrchy = "displayParItemHrchy";
  const custAttr = "custAttr";
  const itemAttr = "itemAttr";

  if (size(result?.[displayParCustHrchy]) > 0) {
    for (let i = 0; i < result[displayParCustHrchy].length; i ++) {
      row[displayParCustHrchy + String(i + 1).padStart(2, "0")] = result[displayParCustHrchy][i];
    }
  }

  if (size(result?.[displayParItemHrchy]) > 0) {
    for (let i = 0; i < result[displayParItemHrchy].length; i ++) {
      row[displayParItemHrchy + String(i + 1).padStart(2, "0")] = result[displayParItemHrchy][i];
    }
  }

  if (size(result?.[custAttr]) > 0) {
    for (let i = 0; i < result[custAttr].length; i ++) {
      row[custAttr + i] = result?.[custAttr]?.[i];
    }
  }

  if (size(result?.[itemAttr]) > 0) {
    for (let i = 0; i < result.itemAttr.length; i ++) {
      row[itemAttr + i] = result?.[itemAttr]?.[i];
    }
  }

  const readyToSellInDate = result.readyToSellinDt !== null ? new ZDate(result.readyToSellinDt) : null;
  if (readyToSellInDate !== null) {
    readyToSellInDate.setHours(0);
  }
  row.readyToSellinDt = readyToSellInDate;

  const endOfSalesDate = result.endOfSalesDt !== null ? new ZDate(result.endOfSalesDt) : null;
  if (endOfSalesDate !== null) {
    endOfSalesDate.setHours(0);
  }
  row.endOfSalesDt = endOfSalesDate;
};

const setCustomRowVisible = (datas, p13nData, row) => {
  let isVisible = true;

  if (!isEmpty(p13nData)) {
    if (!p13nData.includes(row.measureCd)) {
      isVisible = false;
    }
  }

  if (isVisible) {
    datas.push(row);
  }
};

const getMeasureDisplayName = (measureCd, measureNm, displayNames) => {
  const canonicalNm = measureCd ? measureCode?.[measureCd]?.name : null;
  const custom = (canonicalNm ? displayNames?.[canonicalNm] : null)
    ?? (measureNm ? displayNames?.[measureNm] : null);
  return custom && !isEmpty(custom) ? custom : measureNm;
};

const setGridDatas = (metaData, formatHeaderList, userData, singleMeasure, displayNames) => {
  const datas = [];
  if (!metaData) {
    return datas;
  }
  const results = metaData.pivotdata;
  const measureDict = metaData.measureDict;
  const p13nData = metaData.visibleMeasures;
  let layoutConfig = null;

  for (const result of results) {
    if (singleMeasure && singleMeasure !== result.measureCd) {
      continue;
    }

    const measure = measureDict[result.measureCd];
    if (measure === null) {
      continue;
    }
    const row = {
      custlevelId: result.customerHrchyNm,
      productlevelId: result.itemHrchyNm,
      measure: getMeasureDisplayName(result.measureCd, measure?.name, displayNames),
      measureSeq: measure?.seqNum,
      total: result.pivotData ? result.pivotData.reduce((a, b) => a + b, 0) : 0,
      editableRow: result.editable && result.editableCustomerHrchy,
      measureEditable: false,
      desc: 1,
      editableCustomerHrchy: result.editableCustomerHrchy,
      resultRow: result,
      customerCd: result.customerHrchyCd,
      itemCd: result.itemHrchyCd,
      invClassCd: result.invClassCd,
      currency: metaData.currency,
      currencySymbol: userData?.currencySymbol,
      // excel
      custLevelCd: metaData.custLevelCd,
      prodLevelCd: metaData.itemLevelCd,
      versionId: `${metaData.searchVersion.id}n`,
      measureCd: result.measureCd,
      timeDimension: metaData.timeDimension,
    };

    if (metaData.layoutId) {
      layoutConfig = metaData.layoutInfos.layoutConfig ?? metaData.layoutInfos;
      if (size(layoutConfig) > 0) {
        row.measureSeq = layoutConfig?.activeMeasures?.[result.measureCd]?.seqNum ?? 0;
      }
    }
    if (result.measure.computeFlg) {
      row.total = result.computeTotal;
    }

    for (let i = 0; i < formatHeaderList.length; i++) {
      const header = formatHeaderList[i];
      if (result.pivotData) {
        row[header] = (result.pivotData[i] !== null && result.pivotData[i] !== undefined && !isNaN(result.pivotData[i]) && result.pivotData[i] !== 0)
            ? parseFloat(NumberUtil.formatNumber(result.pivotData[i], { maximumFractionDigits: 2, minimumFractionDigits: 0 }).replace(/,/g, ""))
            : null;
      }
    }
    setAttrGridData(row, result);
    setCustomRowVisible(datas, p13nData, row);
  }
  return datas;
};

const makeDataMap = result => {
  const { timeDimension, formatHeaders } = result;
  const resultMap = {};

  for (const row of result?.results) {
    const measureCd = row?.measure?.measureCd ?? row?.measureCd;
    const rowKey = `${row.customerHrchyCd}${row.itemHrchyCd}${measureCd}`;
    let position = 0;

    const headers = row?.headers ?? formatHeaders;
    for (const header of headers) {
      let format = dateUtils.headerDateFormatter(new ZDate(header), timeDimension);
      if (codeUtil.isWeekBucket(timeDimension)) {
        format = formatHeaders[format] ?? format;
      }
      const key = `${rowKey}${format}`;
      if (!row.pivotData) {
        resultMap[key] = 0;
      } else {
        if (row.pivotData.length > position) {
          resultMap[key] = row.pivotData.at(position) && !isNaN(row.pivotData.at(position)) ? parseFloat(NumberUtil.formatNumber(row.pivotData.at(position), {maximumFractionDigits: 2, minimumFractionDigits: 0}).replace(/,/g,"")) : 0;
        } else {
          resultMap[key] = 0;
        }
      }
      position++;
    }
  }
  return resultMap;
};

const totalValueGetter = (param) => {
  const data = param?.data;
  if (!data) {
    return 0;
  }

  const contextHeaderList = param?.context?.headerList;
  const resolvedHeaderList = isArray(contextHeaderList)
    ? contextHeaderList
    : (contextHeaderList && typeof contextHeaderList === "object" ? values(contextHeaderList) : []);

  const isPrice = data.resultRow?.measure?.valueTypeCd === PRICE;
  const totalList = [];
  let count = 0;

  if (resolvedHeaderList.length > 0) {
    for (const col of resolvedHeaderList) {
      if (data[col] !== null && data[col] !== undefined && !isNaN(data[col])) {
        totalList.push(Number(data[col]));
        if (isPrice) {
          count++;
        }
      } else {
        totalList.push(null);
      }
    }
  } else if (isArray(data.resultRow?.pivotData)) {
    // Hidden export grid can miss context.headerList; fallback to raw pivot values.
    for (const val of data.resultRow.pivotData) {
      if (val !== null && val !== undefined && !isNaN(val)) {
        totalList.push(Number(val));
        if (isPrice) {
          count++;
        }
      } else {
        totalList.push(null);
      }
    }
  }

  const numericTotal = totalList.reduce((sum, value) => sum + Number(value), 0);
  if (isPrice && count !== 0) {
    return Number((numericTotal / count).toFixed(2));
  }
  return Number(numericTotal.toFixed(2));
};

/* INV_DAYS_BOH/INV_DAYS_EOH 는 QTY 타입이지만 재고일수라 정수로 잘리면 의미가 없어서,
   소수점 둘째자리까지 그대로 보여준다(다른 QTY/AMT 측정치는 정수로 잘라서 표시). */
const DECIMAL_QTY_MEASURE_CDS = ["INV_DAYS_BOH", "INV_DAYS_EOH"];

const numberValueFormatBlank = param => {
  const expression = param?.data?.resultRow?.customerCd ? param?.data?.resultRow?.customerCurrencyCd : param?.data?.resultRow?.hrchyCurrencyCd;
  const symbol = getCurrencySymbol(expression) ?? param?.data?.currencySymbol ?? "";
  if (param?.value === null || param?.value === undefined || param?.valueFormatted === null || param?.valueFormatted === undefined) {
    return "";
  }
  const colId = param?.column?.getColId?.() ?? param?.colDef?.field;
  const paramMeasureType = param?.data?.resultRow?.measure?.valueTypeCd;
  const paramMeasureCd = param?.data?.resultRow?.measure?.measureCd;
  const isDecimalQtyMeasure = DECIMAL_QTY_MEASURE_CDS.includes(paramMeasureCd);
  /* INV_DAYS_BOH/EOH 가 미래 수요 horizon 부족으로 확정값이 아닌 경우("최소 이 정도") — 0이라도 blank 처리하지 않고
     "+" 를 붙여 보여준다. horizonExceededFlg 는 noteFlg 와 동일하게 headers 와 index-aligned 된 boolean list. */
  let horizonExceeded = false;
  if (isDecimalQtyMeasure) {
    const resultRow = param?.data?.resultRow;
    const index = resultRow?.headers?.indexOf(colId) >= 0 ? resultRow.headers.indexOf(colId) : param?.context?.headerList?.indexOf(colId);
    horizonExceeded = !!resultRow?.horizonExceededFlg?.[index];
  }
  if (colId !== "total" && (param?.value === 0 || param?.valueFormatted === 0) && !horizonExceeded) {
    return "";
  }
  let formatedValue = param.valueFormatted;
  if (isDecimalQtyMeasure) {
    /* 공용 decimalPointColumn 포맷터는 maximumFractionDigits만 있어 정수값이면 소수점이 안 붙는다.
       재고일수는 항상 둘째자리까지 보여야 하므로 여기서 직접 고정폭으로 포맷한다. */
    formatedValue = NumberUtil.formatNumber(param.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (horizonExceeded) {
      formatedValue = `${formatedValue}+`;
    }
  } else if (isEqual(paramMeasureType, AMT) || isEqual(paramMeasureType, QTY)) {
    if (formatedValue?.toString().includes(".")) {
      formatedValue = split(param.valueFormatted, ".")[0];
    }
  }
  if (isEqual(paramMeasureType, AMT) || isEqual(paramMeasureType, PRICE)) {
    if (param?.value < 0) {
      return `-${symbol}${replace(formatedValue, "-", "")}`;
    }
    return symbol + formatedValue;
  }
  return formatedValue;
};

const changeExpression = (compute, measureList, isCode) => {
  const changeFrom = isCode ? "measureCd" : "name";
  const changeTo = isCode ? "name" : "measureCd";
  let expression = compute;

  for (const measure of measureList) {
    if (!isCode) {
      expression = expression.replaceAll(`[${GridUtils.gridValueL10N(measure[changeFrom])}]`, `[${measure[changeTo]}]`);
    } else {
      expression = expression.replaceAll(`[${measure[changeFrom]}]`, `[${GridUtils.gridValueL10N(measure[changeTo])}]`);
    }
  }
  return expression;
};

const getSourceMeasure = (initialValue) => {
  const { SA_QTY, YOY_QTY, MF_QTY, SF_QTY, BF_QTY, FF_QTY, SALES_PLAN_QTY } = codeUtil.dpMeasure;
  const { LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, FINANCE_PLAN, SALES_PLAN_MEASURE } = codeUtil.dpInitalValues;

  const measures = [];
  if (isEqual(initialValue, LAST_ACTUAL_SALES)) {
    measures.push(SA_QTY.cd);
    measures.push(SA_QTY.cd);
  } else if (isEqual(initialValue, ACTUAL_SALES_YOY)) {
    measures.push(YOY_QTY.cd);
    measures.push(YOY_QTY.cd);
  } else if (isEqual(initialValue, LAST_RELEASED_VERSION) || isEqual(initialValue, "dpVersion")) {
    measures.push(MF_QTY.cd);
    measures.push(SF_QTY.cd);
  } else if (isEqual(initialValue, "baselineForecast")) {
    measures.push(BF_QTY.cd);
    measures.push(BF_QTY.cd);
  } else if (isEqual(initialValue, FINANCE_PLAN)) {
    measures.push(FF_QTY.cd);
    measures.push(FF_QTY.cd);
  } else if (isEqual(initialValue, SALES_PLAN_MEASURE)) {
    measures.push(SALES_PLAN_QTY.cd);
    measures.push(SALES_PLAN_QTY.cd);
  }
  return measures;
}

const getDefaultComputeList = (initialValue) => {
  const defaultMeasure = codeUtil.dpComputeDefaultMeasure;
  const defaultComputeList = [];
  const sourceQtyMeasure = getSourceMeasure(initialValue);
  if (size(sourceQtyMeasure) < 1) {
    return [];
  }
  let targetMeasureCds = keys(defaultMeasure);
  for (let i = 0; i < targetMeasureCds.length; i ++) {
    const measureCd = targetMeasureCds[i];
    const computeObj = {};
    computeObj[measureCd] = `[${sourceQtyMeasure[i]}]*1`;
    defaultComputeList.push(computeObj);
  }

  return defaultComputeList;
}

const MeasureFormulaBox = ({ computeMeasureList, oriMeasureList }) => {
  if (size(oriMeasureList) > 0) {
    oriMeasureList.push(codeUtil.dpMeasure.SUGGESTED_MP_QTY);
  }
  return (
  <Box
    sx={{ width: "90%", p: 1, pl: 2, mt: 2, backgroundColor: "#eee", borderRadius: 1, boxSizing: "border-box" }}>
   {size(computeMeasureList) > 0 ?
    computeMeasureList?.map((data) => {
      const formula = `[${keys(data)[0]}]: ${values(data)[0]}`;
      const transFormula = changeExpression(formula, oriMeasureList, true).replace(/\[|\]/, '');
      return (
        <Typography
          sx={{ fontSize: FS.H2 }}
          key={transFormula}
        >
          {transFormula.replace(/\[|\]/, '')}
        </Typography>
      )
    }) :
    <Typography sx={{ fontSize: FS.H2 }}>
      {t("MSG_NoAppliedFormula")}
    </Typography>
   }
  </Box>)
}

const workbenchCds = {
  CUSTOMER_HRCHY: "CUSTOMER_HRCHY",
  CUSTOMER_LEVEL: "CUSTOMER_LEVEL",
  CUSTOMER_CODE: "CUSTOMER_CODE",
  CUSTOMER_ATTR: "CUSTOMER_ATTR",
  ITEM_HRCHY: "ITEM_HRCHY",
  ITEM_LEVEL: "ITEM_LEVEL",
  ITEM_CODE: "ITEM_CODE",
  ITEM: "item",
  PROD_LEVEL: "productlevel",
  CUST_LEVEL: "custlevel",
  CUST_LEVEL_ID: "custlevelId",
  PROD_LEVEL_ID: "productlevelId",
  CUST_CD: "customerCd",
  CUST_CD_ID: "custCdId",
  ITEM_CD: "itemCd",
  ITEM_CD_ID: "itemCdId",
  PROD_ATTR: "productAttr",
  INV_CLASS_CD: "invClassCd"

}

/*
 * 채널 재고 수식 지표 (GitHub #44) — 백엔드 DpCubeJdbcService.getChannelInventoryFormulaMeasures 와
 * 반드시 같은 규칙으로 유지한다:
 *   BOH(T) = EOH(T-1)                                                    — 항상
 *   TARGET_INV_QTY_EOH(T) = TARGET_INV_DAYS 를 T+1부터 향후 POS_QTY 로 채운 수량 — POS_QTY 편집에 반응
 *   SUGGESTED_MP_QTY(T-1) = TARGET_INV_QTY_EOH(T) - BOH(T) + POS_QTY(T)          — boh/target 이 있을 때만
 *   EOH(T) = INV_QTY(T)                                                   — T < versionStartDate (실측)
 *          = BOH(T) + SUGGESTED_MP_QTY(T-1) - POS_QTY(T)                    — T >= versionStartDate (계획, 재귀)
 *            (SUGGESTED_MP_QTY(T-1) 을 못 구하면 원본 MF_QTY(T-1)로 fallback — 이건 실제 계획값이라 POS 아님)
 *   재고일수(BOH,T) = BOH(T) 를 T월부터 향후 POS_QTY 로 순차 소모한 일수
 *   재고일수(EOH,T) = EOH(T) 를 T+1월부터 향후 POS_QTY 로 순차 소모한 일수
 * TARGET_INV_DAYS(콤보별 고정값)만 서버값을 그대로 읽고, TARGET_INV_QTY_EOH/SUGGESTED_MP_QTY/EOH/BOH/
 * 재고일수는 전부 여기서 다시 계산해 grid에 반영한다 — 그래서 POS_QTY 또는 MF_QTY 편집 어느 쪽이든
 * 이 체인 전체가 다시 흐른다.
 */
/*
 * horizonExceeded=true 면 futureDemand(화면에 로드된 미래 버킷) 를 다 써도 재고가 안 떨어져
 * days 가 확정값이 아니라 "최소 이 정도"라는 뜻이다 — 백엔드 calcInventoryDays 와 동일한 규칙
 * (DpCubeJdbcService.InventoryDaysResult). 호출부가 이 값으로 resultRow.horizonExceededFlg 를
 * 갱신해야 "+" 표시가 재계산 후에도 맞게 남는다.
 * futureDates 는 futureDemand 와 index-aligned — 각 미래 버킷(WEEK/MONTH/QUARTER)이 실제로 며칠에
 * 걸쳐있는지를 dateUtils.daysInBucket 으로 구해서 쓴다(선택된 버킷 단위와 무관하게 항상 "그 달 일수"로
 * 뭉개던 예전 방식은 WEEK/QUARTER 버킷에서 재고일수를 크게 부풀렸다).
 */
const calcChannelInventoryDays = (qty, futureDemand, futureDates, bucket, startDay) => {
  let remaining = qty;
  let days = 0;
  for (let i = 0; i < futureDemand.length; i++) {
    const demand = futureDemand[i];
    const bucketDays = dateUtils.daysInBucket(futureDates[i], bucket, startDay);
    if (demand <= 0) {
      days += bucketDays;
      continue;
    }
    if (remaining >= demand) {
      days += bucketDays;
      remaining -= demand;
    } else {
      days += (remaining / demand) * bucketDays;
      remaining = 0;
      return { days, horizonExceeded: false };
    }
  }
  return { days, horizonExceeded: remaining > 0 };
};

/*
 * INV_DAYS_BOH/EOH 재계산 결과의 horizonExceeded 를 그 행의 resultRow.horizonExceededFlg 에 반영한다 —
 * noteFlg 와 동일하게 headers 와 index-aligned 된 boolean list(WorkbenchUtil.numberValueFormatBlank 가
 * 이 배열을 읽어 "+" 를 붙인다). 서버 응답에 이 배열이 없던 행(과거엔 exceeded 가 전혀 없었던 콤보)도
 * 있을 수 있어 없으면 새로 만든다.
 */
const setHorizonExceededFlg = (rowNode, index, exceeded) => {
  const resultRow = rowNode?.data?.resultRow;
  if (!resultRow) {
    return;
  }
  if (!resultRow.horizonExceededFlg) {
    resultRow.horizonExceededFlg = [];
  }
  resultRow.horizonExceededFlg[index] = exceeded;
};

/*
 * calcChannelInventoryDays 의 역방향 — 백엔드 DpCubeJdbcService.accumulateQtyForDays 와 동일한 규칙.
 * 목표 재고일수(targetDays)에 도달할 때까지 futureDemand 를 선택된 버킷의 실제 일수 기준으로 누적한 수량.
 * futureDates 는 futureDemand 와 index-aligned.
 */
const accumulateQtyForDays = (targetDays, futureDemand, futureDates, bucket, startDay) => {
  let remainingDays = targetDays;
  let qty = 0;
  for (let i = 0; i < futureDemand.length; i++) {
    const demand = futureDemand[i];
    const bucketDays = dateUtils.daysInBucket(futureDates[i], bucket, startDay);
    if (remainingDays <= 0) {
      break;
    }
    if (remainingDays >= bucketDays) {
      qty += demand;
      remainingDays -= bucketDays;
    } else {
      qty += demand * (remainingDays / bucketDays);
      remainingDays = 0;
    }
  }
  return qty;
};

/*
 * editedRowNode(POS_QTY 또는 MF_QTY 행)와 같은 콤보(그룹)의 형제 행들 중 INV_BOH_QTY/INV_EOH_QTY/
 * INV_DAYS_BOH/INV_DAYS_EOH 를 편집된 날짜부터 구간 끝까지 재계산해 grid 에 반영한다.
 * 채널 지표 행이 없는 콤보(다른 화면 재사용)면 아무 것도 하지 않는다.
 *
 * @param leafRowNodes    e.node.parent.allLeafChildren — 같은 콤보의 모든 measure 행
 * @param formatGridHeader 날짜 컬럼 colId 목록(오름차순)
 * @param resolveColumnDate colId -> 실제 Date 로 바꾸는 함수(주 버킷은 reverseHeader 매핑 필요 — 호출부에서 처리)
 * @param versionStartDate 버전 시작일(Date) — 이 날짜 이전은 INV_QTY 실측, 이후는 재귀식
 * @param getCellValue    (colId, rowNode) => value
 * @param editedMeasureCd 실제로 편집된 measureCd("POS_QTY" | "MF_QTY") — TARGET_INV_QTY_EOH/재고일수는
 *                        향후 POS_QTY에만 좌우되므로, POS_QTY가 편집된 경우에만 재계산하고 MF_QTY 편집일
 *                        땐 기존 값을 그대로 읽기만 한다(안 그러면 그리드에 표시된 반올림된 POS_QTY로
 *                        다시 계산하면서 원래 값과 미세하게 달라져 "안 바꾼 값이 바뀐 것"처럼 보인다).
 * @param timeDimension   현재 선택된 버킷(WEEK/MONTH/QUARTER) — 재고일수/목표재고수량 계산의 day-span 기준
 * @param startDay        주 버킷(WEEK/PARTIAL_WEEK)의 시작 요일 — dateUtils.daysInBucket 에 그대로 전달
 */
const recalcChannelMeasures = (leafRowNodes, formatGridHeader, resolveColumnDate, versionStartDate, getCellValue, editedMeasureCd, timeDimension, startDay) => {
  const rowByMeasureCd = {};
  for (const rowNode of leafRowNodes ?? []) {
    const measureCd = rowNode.data?.resultRow?.measureCd;
    if (measureCd) {
      rowByMeasureCd[measureCd] = rowNode;
    }
  }

  const posRow = rowByMeasureCd.POS_QTY;
  const bohRow = rowByMeasureCd.INV_BOH_QTY;
  const eohRow = rowByMeasureCd.INV_EOH_QTY;
  const invDaysBohRow = rowByMeasureCd.INV_DAYS_BOH;
  const invDaysEohRow = rowByMeasureCd.INV_DAYS_EOH;
  const suggestedRow = rowByMeasureCd?.SUGGESTED_MP_QTY;
  const targetRow = rowByMeasureCd?.TARGET_INV_QTY_EOH;
  const targetDaysRow = rowByMeasureCd?.TARGET_INV_DAYS;
  const mfRow = rowByMeasureCd?.MF_QTY;
  const saRow = rowByMeasureCd?.SA_QTY;
  const mfVsSuggestedRow = rowByMeasureCd?.MF_VS_SUGGESTED_QTY;

  // 이 콤보에 채널 지표가 없으면(채널 화면이 아닌 곳에서 재사용되는 경우) 건드리지 않는다
  if (!posRow || !eohRow) {
    return;
  }

  const posByCol = colId => Number(getCellValue(colId, posRow)) || 0;
  const eohByCol = colId => eohRow ? (Number(getCellValue(colId, eohRow)) || 0) : 0;
  const bohByCol = colId => bohRow ? Number(getCellValue(colId, bohRow)) : null;
  const mfByCol = colId => mfRow ? (Number(getCellValue(colId, mfRow)) || 0) : 0;
  const saByCol = colId => saRow ? (Number(getCellValue(colId, saRow)) || 0) : 0;
  // 목표재고일수(target_inv_days)는 콤보별 고정값이라 매 버킷에 동일한 값이 실려있다.
  const targetDaysByCol = colId => targetDaysRow ? (Number(getCellValue(colId, targetDaysRow)) || 0) : null;

  // 서버가 조회 시작일보다 한 달 전부터 데이터를 가져와서 계산하기 때문에(getChannelInvQty),
  // 화면에 보이는 첫 버킷도 BOH가 항상 존재한다 — 그 앞 버킷이 화면에 없어 재귀로 못 구하니
  // 서버가 이미 계산해 로드해준 첫 버킷의 INV_BOH_QTY 값을 그대로 시작점으로 쓴다.
  let prevEoh = formatGridHeader.length > 0 ? bohByCol(formatGridHeader[0]) : null;
  for (let i = 0; i < formatGridHeader.length; i++) {
    const colId = formatGridHeader[i];
    let boh = prevEoh;
    if (boh != null && boh < 0) {
      boh = 0;
    }
    const posQty = posByCol(colId);
    const prevCol = i > 0 ? formatGridHeader[i - 1] : null;

    const columnDate = resolveColumnDate(colId);
    const isPlanPeriod = versionStartDate && columnDate && columnDate >= versionStartDate;

    const futurePosFromTPlus1 = [];
    const futureDatesFromTPlus1 = [];
    for (let j = i + 1; j < formatGridHeader.length; j++) {
      futurePosFromTPlus1.push(posByCol(formatGridHeader[j]));
      futureDatesFromTPlus1.push(resolveColumnDate(formatGridHeader[j]));
    }

    // 버전 시작일 이전(실측 구간)은 INV_BOH_QTY/INV_EOH_QTY 값만 있으면 되고, TargetInvQty/재고일수/
    // SuggestedMP 같은 계획성 파생 지표는 계획 구간(isPlanPeriod)에서만 재계산해서 grid에 반영한다.
    // TargetInvQty(T) 는 T+1부터의 향후 POS_QTY 만으로 계산되어 MF_QTY와는 무관하다.
    // Target inv qty는 target inv days 에서만 변경됌.
    // POS_QTY가 편집됐을 때만 다시 계산해서 grid에 반영하고, MF_QTY 편집일 땐 그리드에 이미 있는
    // 값을 그대로 읽기만 한다(반올림된 표시값으로 재계산하면 원래 값과 미세하게 달라질 수 있어서).
    let targetQty;
    if (isPlanPeriod && editedMeasureCd === "POS_QTY") {
      const targetDays = targetDaysByCol(colId);
      targetQty = (targetRow && targetDays !== null) ? accumulateQtyForDays(targetDays, futurePosFromTPlus1, futureDatesFromTPlus1, timeDimension, startDay) : null;
      if (targetRow && targetQty !== null) {
        targetRow.setDataValue(colId, targetQty);
      }
    } else {
      targetQty = targetRow ? Number(getCellValue(colId, targetRow)) : null;
    }

    // SuggestedMarketPlanQty(T-1) 재계산 — boh(=BOH(T))는 항상 있지만(위에서 첫 버킷도 서버값으로
    // 시드해둠), TargetInvQty(T)를 못 구한 경우(계층 롤업이라 target_inv_days가 없는 콤보, 또는
    // 계획 구간이 아닌 경우)에만 원본 MF_QTY(T-1)로 fallback한다.
    let suggestedMpPrev = 0;
    if (isPlanPeriod && boh !== null && targetQty !== null) {
      suggestedMpPrev = targetQty - boh + posQty;
      if (suggestedMpPrev < 0) {
        suggestedMpPrev = 0;
      }
      if (suggestedRow && prevCol) {
        suggestedRow.setDataValue(prevCol, suggestedMpPrev);
        // SUGGESTED_MP_QTY(T-1) 가 바뀌었으니 "매니저 수요계획 vs 제안값"(MF_QTY-SUGGESTED_MP_QTY) 도
        // 그 칸(T-1)에서 같이 다시 계산한다 — 버전 시작일 이후(계획 구간)에서만 의미 있는 비교라
        // T-1 자신이 계획 구간일 때만 반영한다.
        if (mfVsSuggestedRow) {
          const prevDate = resolveColumnDate(prevCol);
          const prevIsPlanPeriod = versionStartDate && prevDate && prevDate >= versionStartDate;
          if (prevIsPlanPeriod) {
            mfVsSuggestedRow.setDataValue(prevCol, mfByCol(prevCol) - suggestedMpPrev);
          }
        }
      }
    }

    // EOH(T) = BOH(T) + MP(T-1) - POS(T). MP(T-1)은 SuggestedMarketPlan 재귀값이 아니라 원본 측정값을
    // 그대로 읽는다(백엔드 DpCubeJdbcService.getChannelInventoryFormulaMeasures 와 동일한 규칙):
    // T-1이 아직 계획 구간이 아니면(첫 예측월의 전월, 그 달엔 C2(MF_QTY)가 존재하지 않는다)
    // E2(SA_QTY, Historical Sale-In)로 대체하고, T-1이 계획 구간이면 C2(MF_QTY)를 그대로 쓴다.
    let mpPrev = 0;
    if (prevCol) {
      const prevDate = resolveColumnDate(prevCol);
      const prevIsPlanPeriod = versionStartDate && prevDate && prevDate >= versionStartDate;
      mpPrev = prevIsPlanPeriod ? mfByCol(prevCol) : saByCol(prevCol);
    } else {
      // 조회 구간의 첫 컬럼(T0)은 T0-1이 그리드 밖이라 컬럼으로 못 읽는다 — 서버가 INV_EOH_QTY row 에만
      // 실어보낸 판매실적수량(SA_QTY, T0-1)을 그대로 쓴다(백엔드는 이 값을 EOH 계산에만 쓰므로 eohRow의
      // resultRow 에만 있다 — DpCubeJdbcService.getChannelInventoryFormulaMeasures 참고).
      mpPrev = Number(eohRow?.data?.resultRow?.prevSalesQty) || 0;
    }
    let eoh = isPlanPeriod ? (boh ?? 0) + mpPrev - posQty : eohByCol(colId);
    if (eoh < 0) {
      eoh = 0;
    }
    eohRow.setDataValue(colId, eoh);

    if (isPlanPeriod && invDaysEohRow) {
      const { days: invDaysEoh, horizonExceeded: eohExceeded } = calcChannelInventoryDays(eoh, futurePosFromTPlus1, futureDatesFromTPlus1, timeDimension, startDay);
      setHorizonExceededFlg(invDaysEohRow, i, eohExceeded);
      invDaysEohRow.setDataValue(colId, invDaysEoh);
    }

    if (boh !== null) {
      if (bohRow) {
        bohRow.setDataValue(colId, boh);
      }
      if (isPlanPeriod && invDaysBohRow) {
        const futurePosFromT = [posQty, ...futurePosFromTPlus1];
        const futureDatesFromT = [columnDate, ...futureDatesFromTPlus1];
        const { days: invDaysBoh, horizonExceeded: bohExceeded } = calcChannelInventoryDays(boh, futurePosFromT, futureDatesFromT, timeDimension, startDay);
        setHorizonExceededFlg(invDaysBohRow, i, bohExceeded);
        invDaysBohRow.setDataValue(colId, invDaysBoh);
      }
    }

    prevEoh = eoh;
  }
};

const WorkbenchUtil = {
  excelStyles,
  defaultGridSetting,
  getSearchFormat,
  chartDataSeries,
  setChartDatas,
  setGridDatas,
  getMeasureDisplayName,
  makeDataMap,
  totalValueGetter,
  numberValueFormatBlank,
  ableDayOfWeek,
  ableEndDayOfWeek,
  ableMonthOfQuerter,
  ableEndMonthOfQuerter,
  changeExpression,
  getComputeObj,
  getDefaultComputeList,
  MeasureFormulaBox,
  setAttrGridData,
  recalcChannelMeasures,
  workbenchCds
};
export default WorkbenchUtil;